import { useEffect, useState } from "react";

import {
  Button,
  RadioButton,
  TextArea,
  Table,
  TableMetadata,
  Typography,
  useTable,
  Label,
} from "@king-ict/eupisi-ui/components";
import { hrDate } from "@king-ict/eupisi-ui/utils";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import { DEFAULT_INSTITUTION_COMMITTEE } from "./constants";
import {
  getCommitteeEmployeeDisplayName,
  isEmployeeActive,
  updateEmployeesWithPresident,
  updateCommitteeEmployee,
  setupCommitteeEmployees,
  generatingEditedEmployee,
  replaceEmployeeInTableData,
} from "./utils";
import {
  getInstitutionCommitteeById,
  createInstitutionCommitteeById,
  saveInstitutionCommittee,
} from "../../api/Committee/InstitutionCommitteeApi";
import { useAppDispatch } from "../../app/store";
import {
  ContentArea,
  FormCard,
  ToolbarEdit,
  ToolbarTop,
} from "../../components";
import { useEnumCodeBooks } from "../../hooks/useEnumCodeBooks";
import { ValidationFailure } from "../../models";
import {
  CommitteeEmployee,
  CommitteeTypeEnum,
  InstitutionCommittee,
  CommitteeType,
} from "../../models/Committee";
import { SaveResult } from "../../models/Shared";
import { committeeDetailUrl } from "../../routes/routes";
import { resetIsEdited, setIsEdited } from "../../slices/applicationSlice";
import { FormatUtils } from "../../utils";
import { useDeleteCommittee } from "./hooks/useDeleteCommittee";
import { CommitteeModal } from "./components/CommitteeModal";

export function CommitteeEdit() {
  const { id, institutionId } = useParams<{
    id: string;
    institutionId: string;
  }>();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [item, setItem] = useState<InstitutionCommittee>(
    DEFAULT_INSTITUTION_COMMITTEE
  );
  const [tableData, setTableData] = useState<CommitteeEmployee[]>([]);
  const tableMetadata: TableMetadata<CommitteeEmployee> = {
    rowKey: "rowKeyId",
    columns: [
      {
        headerClassName: "col-4",
        columnKey: "expertCommissionRole",
        header: t("common.expertCommission.role"),
        accessor: (item: CommitteeEmployee) => {
          return <div>{item.committeeRole?.name}</div>;
        },
      },
      {
        headerClassName: "col-6",
        columnKey: "employee",
        header: t("common.expertCommission.employee"),
        accessor: (item: CommitteeEmployee) => {
          return <div>{getCommitteeEmployeeDisplayName(item)}</div>;
        },
      },
      {
        headerClassName: "col-4",
        columnKey: "activeTo",
        header: t("common.expertCommission.activeTo"),
        accessor: (item) => {
          return <div>{FormatUtils.formatDate(item.deactivationDate)}</div>;
        },
      },
      {
        columnKey: "edit",
        header: t("common.edit"),
        headerClassName: "col-1",
        accessor: (employee) => {
          return (
            <div>
              <Button
                size="sm"
                outline
                onClick={() => handleDetailsClicked(employee)}
                disabled={
                  employee.committeeRole.committeeType !==
                  CommitteeTypeEnum.Institution
                }
              >
                {t("common.edit")}
              </Button>
            </div>
          );
        },
      },
      {
        columnKey: "president",
        header: t("common.expertCommission.president"),
        headerClassName: "col-1",
        accessor: (emp: CommitteeEmployee) => {
          const isActive = isEmployeeActive(emp);

          return (
            <div className="pt-2 ml-3">
              <RadioButton
                ariaLabel="item.president"
                key={emp.rowKeyId}
                name={"item.president"}
                checked={emp.isPresident}
                onChange={() => handlePresidentChange(emp.rowKeyId!)}
                value={emp.rowKeyId!}
                disabled={
                  !(
                    emp.employee &&
                    emp.committeeRole.committeeType ===
                      CommitteeTypeEnum.Institution
                  ) || !isActive
                }
              />
            </div>
          );
        },
      },
    ],
    rowToolTip: () => undefined,
  };

  const [tableProps] = useTable({
    metadata: tableMetadata,
    data: tableData,
    pagination: false,
    totalItems: 1,
    defaultSort: {},
  });
  const [errors, setErrors] = useState<ValidationFailure[]>();
  const { committeeStatusCodeBook: statuses } = useEnumCodeBooks();
  const [editing, setEditing] = useState<CommitteeEmployee | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  const { handleDeleteCommittee, isDeleteEnabled } = useDeleteCommittee({
    isEnabled: !item.hasDecisions,
    committeeType: "institution",
    committeeId: +id,
  });

  useEffect(() => {
    (async () => {
      if (!isNaN(+id) && +id > 0) {
        const result = await getInstitutionCommitteeById(+id);
        if (result) {
          setupCommitteeEmployees(result, setItem, setTableData);
        }
      }
      if (!isNaN(+institutionId) && +institutionId > 0) {
        const result = await createInstitutionCommitteeById(+institutionId);
        if (result) {
          setupCommitteeEmployees(result, setItem, setTableData);
        }
      }
    })();
    dispatch(resetIsEdited());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, institutionId]);

  const handleDetailsClicked = (
    expertCommissionEmployee: CommitteeEmployee
  ) => {
    setEditing(expertCommissionEmployee);
  };

  const handlePresidentChange = (rowKeyId: CommitteeEmployee["rowKeyId"]) => {
    const employees = updateEmployeesWithPresident(tableData, rowKeyId);

    setTableData(employees);
    const newItem: InstitutionCommittee = {
      ...item,
      employees: employees,
    };
    setItem(newItem);
    dispatch(setIsEdited());
  };
  const handleNoteChange = (e: any) => {
    const note = e.target.value;
    const newItem: InstitutionCommittee = {
      ...item,
      note: note,
    };
    setItem(newItem);
    dispatch(setIsEdited());
  };

  const handleOnCloseModal = () => {
    setEditing(null);
  };

  const handleOnSaveCommitteeEmployee = (
    newCommitteeEmployee: CommitteeEmployee
  ) => {
    if (!editing) return;

    const employeeToEdit = generatingEditedEmployee(
      editing,
      newCommitteeEmployee
    );

    setTableData((prev) => replaceEmployeeInTableData(prev, employeeToEdit));
    setItem((currItem) => updateCommitteeEmployee(currItem, employeeToEdit));
    setEditing(null);
    setIsEditing(true);
  };

  return (
    <ContentArea>
      <ToolbarTop
        showAdd={"hide"}
        showBack={"show"}
        showEdit={"hide"}
        pageTitle={t("common.expertCommission.pageNameEdit")}
        errors={errors}
      />

      <FormCard>
        <Row>
          <Col md={2}>
            <Typography variant="base" color="light">
              {t("common.expertCommission.institution")}
            </Typography>
          </Col>
          <Col md={10}>
            <Typography variant="base" color="light">
              {item.institution?.code} - {item.institution?.name}
            </Typography>
          </Col>
        </Row>
        <Row>
          <Col md={2}>
            <Typography variant="base" color="light">
              {t("common.expertCommission.dateFromDateTo")}
            </Typography>
          </Col>
          <Col md={4}>
            <Typography variant="base" color="light">
              {item.dateFrom ? hrDate(new Date(item.dateFrom)) : ""} -{" "}
              {item.dateTo ? hrDate(new Date(item.dateTo)) : ""}
            </Typography>
          </Col>
          <Col md={2}>
            <Typography variant="base" color="light">
              {t("common.status")}
            </Typography>
          </Col>
          <Col md={4}>
            <Label
              color={FormatUtils.getCommitteeStatusColor(item.statusId)}
              className="text-center"
              outline={false}
              fullWidth={false}
            >
              {statuses.find((v) => v.id === item.statusId)?.name}
            </Label>
          </Col>
        </Row>
      </FormCard>

      <FormCard>
        <Table {...tableProps} />
      </FormCard>

      <FormCard>
        <Row>
          <Col md={12}>
            <TextArea
              ariaLabel="item.note"
              label={t("common.expertCommission.note")}
              name={"item.note"}
              value={item.note ?? ""}
              onChange={handleNoteChange}
              errorText={
                errors?.find((e) => e.propertyName === "Note")?.message
              }
              maxLength={5000}
            />
          </Col>
        </Row>
      </FormCard>

      <ToolbarEdit
        showCancel={"show"}
        showSave={"show"}
        isDataEdited={isEditing}
        handleSave={async () => saveInstitutionCommittee(item)}
        redirectUrlSaveOK={committeeDetailUrl}
        setSaveErrors={setErrors}
        onSaveSuccess={(data: SaveResult<InstitutionCommittee>) => {
          if (!data.result) return;

          setItem(data.result);
        }}
        showDelete={isDeleteEnabled ? "show" : "disable"}
        deleteDisabledReason={
          item.hasDecisions
            ? t("common.expertCommission.deleteDisabledHasDecisions")
            : undefined
        }
        onClickDelete={handleDeleteCommittee}
      />
      <CommitteeModal
        committeeType={CommitteeType.Institution}
        editingEmployee={editing ?? null}
        onSave={handleOnSaveCommitteeEmployee}
        onClose={handleOnCloseModal}
      />
    </ContentArea>
  );
}
